import lodashFind from 'lodash/find';
import lodashIsEmpty from 'lodash/isEmpty';

import { baseApi, METHODS } from '../baseApi';
import { queryFnRequest, serializeParams } from '../helpers';
import { ApiArgs, PaginatedResult } from '../types';

import {
  IHaveArrived,
  IHaveArrivedPayload,
  OrderDetails,
  OrderDetailsData,
  OrderDetailsPayload,
  OrdersPayload,
} from './types';

export const TAGS = {
  ORDERS: 'orders',
  ORDER_DETAILS: 'orderDetails',
};

const sortItemByPriceAsc = (data = []) =>
  data.sort((a, b) => (a.price < b.price ? -1 : 1));

export const orderApi = baseApi
  .enhanceEndpoints({ addTagTypes: Object.values(TAGS) })
  .injectEndpoints({
    endpoints: (builder) => ({
      iHaveArrived: builder.mutation<
        IHaveArrived,
        ApiArgs<void, IHaveArrivedPayload>
      >({
        query: (params) => ({
          url: `/user/orders/curbside/${params.payload.id}/arrived`,
          method: 'POST',
          headers: { 'x-api-version': '1' },
          body: {},
        }),
      }),
      getOrderDetails: builder.query<
        OrderDetails,
        ApiArgs<OrderDetailsData, OrderDetailsPayload>
      >({
        providesTags: (_result, _error, params) => [
          {
            id: params.payload?.id || params.payload?.transaction_number,
            type: TAGS.ORDER_DETAILS,
          },
        ],
        queryFn: async (params, api, _extraOptions, baseQuery) => {
          const { id, transaction_number } = params.payload;
          if (!lodashIsEmpty(params.data)) {
            const state = api.getState();
            const pageData =
              // @ts-ignore: RootState
              state?.baseApi?.queries?.[
                `getOrders({"payload":${JSON.stringify(params.data)}})`
              ];
            const result = pageData?.data?.result || [];
            const order = lodashFind(
              result,
              id ? { id } : { transaction_number },
            );
            if (!lodashIsEmpty(order) && order?.status === 'completed') {
              console.log(
                'cache found and order is completed - return the cache data',
              );
              // return data with sorted items by price ascending
              return {
                data: {
                  ...order,
                  items: sortItemByPriceAsc(order?.items),
                } as OrderDetails,
              };
            }
          }
          console.log(
            'no cache found or order is ongoing - request data from api',
          );
          const result = await queryFnRequest<OrderDetails>(baseQuery, {
            url: `/user/orders/${id || transaction_number}`,
            method: METHODS.GET,
            headers: { 'x-api-version': '1' },
          });
          // return data with sorted items by price ascending
          if ('data' in result) {
            return {
              ...result,
              data: {
                ...result.data,
                items: sortItemByPriceAsc(result?.data?.items),
              },
            };
          } else {
            return result;
          }
        },
      }),
      getOrders: builder.query<
        PaginatedResult<OrderDetails>,
        ApiArgs<void, OrdersPayload>
      >({
        providesTags: [TAGS.ORDERS],
        query: (params) => ({
          url: `/user/orders${serializeParams(params.payload)}`,
          method: METHODS.GET,
          headers: { 'x-api-version': '1' },
        }),
        transformResponse: (
          response: PaginatedResult<OrderDetails>,
          _meta,
          params,
        ) => {
          if ('result' in response) {
            return {
              ...response,
              result: response.result.map((r) => ({
                ...r,
                items: sortItemByPriceAsc(r.items), // sort items ascending
                args: params.payload, // add params to each result so we can use it for cache lookup
              })),
            };
          }
          return response;
        },
      }),
    }),
    overrideExisting: true,
  });

export const {
  useGetOrderDetailsQuery,
  useGetOrdersQuery,
  useIHaveArrivedMutation,
} = orderApi;
